/** @file openMenuForSelection: opens the registry menu of the selection at the focused element. */
import type { EditorStore } from '../state/editorTypes';
import { buildMenu } from './menu/buildMenu';
import { treeUiOps } from './menu/treeUiOps';
import type { MenuSurface } from './menu/registryTypes';

/** Where the menu appears: under the focused element, else the middle of the window. */
function anchor(): { x: number; y: number } {
  const el = document.activeElement;
  if (el instanceof HTMLElement && el !== document.body) {
    const r = el.getBoundingClientRect();
    return { x: r.left + 8, y: r.bottom };
  }
  return { x: globalThis.innerWidth / 2, y: globalThis.innerHeight / 2 };
}

/** The panel that has focus decides the surface (and so which chords the menu may show). */
function surfaceOfFocus(): MenuSurface {
  const el = document.activeElement;
  if (el instanceof HTMLElement && el.closest('[data-panel="nodeTree"]') !== null) return 'tree';
  return el instanceof HTMLElement && el.closest('[data-panel="timeline"]') !== null ? 'timeline' : 'viewport';
}

/**
 * ContextMenu / Shift+F10: with selected timeline keys (and focus in the timeline) the key menu of the first one;
 * with a selected node the node menu of the first one; otherwise nothing opens. Returns whether a menu opened.
 */
export function openMenuForSelection(store: EditorStore): boolean {
  const s = store.getState();
  const { x, y } = anchor();
  const surface = surfaceOfFocus();
  const key = s.selection.keys[0];
  if (surface === 'timeline' && key !== undefined) {
    const clipboard = {
      get current() { return store.getState().shell.poseClipboard; },
      set current(value) { store.getState().setShell({ poseClipboard: value }); },
    };
    s.openMenu(x, y, buildMenu('key', { store, surface, node: key.node, frame: key.frame, clipboard }));
    return true;
  }
  const node = s.selection.nodes[0];
  if (node === undefined) return false;
  const view = treeUiOps(store);
  const base = { store, surface: surface === 'timeline' ? 'viewport' : surface, node, confirm: (text: string) => globalThis.confirm(text) } as const;
  s.openMenu(x, y, buildMenu('node-row', view === undefined ? base : { ...base, view }));
  return true;
}
